import React, { useState, useEffect } from 'react';
import { Table } from '../components/ui/Table';
import { Download, ClipboardList, ChevronDown, Search, X, ChevronRight, Package, AlertTriangle, Ban, RefreshCw, MessageSquare } from 'lucide-react';
import {
  listOrdersDetailed,
  exportOrders,
  listRestaurants,
  listGroceries,
  getOrderStats,
  cancelOrder,
  refundOrder,
  updateOrderStatus
} from '../services/resortAdminApi';
import { useAuth } from '../context/AuthContext';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { StatusChangeDialog } from '../components/ui/StatusChangeDialog';

interface OrderItem {
  item_name: string;
  qty: number;
  rate: number;
  amount: number;
}

interface DetailedOrder {
  order_id: string;
  order_date: string;
  status: string;
  resort_name: string;
  pos_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  delivery_address: string;
  order_price: number;
  items: OrderItem[];
  payment_status?: string;
  stripe_payment_id?: string;
  notes?: string;
  additional_details?: string;
}

interface Restaurant {
  name: string;
  restaurant_name: string;
}

interface Grocery {
  name: string;
  grocery_name: string;
}

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: '365', label: 'Last Year' },
  { value: 'all', label: 'All Time' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'ready_for_delivery', label: 'Ready For Delivery' },
  { value: 'out_for_delivery', label: 'Out For Delivery' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'reported', label: 'Reported' },
  { value: 'cc_accepted', label: 'CC Accepted' },
  { value: 'cc_claimed', label: 'CC Claimed' },
  { value: 'cc_ready_for_pickup', label: 'CC Ready For Pickup' },
  { value: 'cc_completed', label: 'CC Completed' },
  { value: 'cc_cancelled', label: 'CC Cancelled' },
  { value: 'cc_reported', label: 'CC Reported' }
];

const PRICE_OPTIONS = [
  { value: '', label: 'All Prices' },
  { value: '0-25', label: 'Under €25' },
  { value: '25-50', label: '€25 - €50' },
  { value: '50-100', label: '€50 - €100' },
  { value: '100+', label: 'Over €100' }
];

export const Orders: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<DetailedOrder[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [groceries, setGroceries] = useState<Grocery[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [stats, setStats] = useState<{ suspended_count: number; suspended_percent: number } | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'cancel' | 'refund'; orderId: string } | null>(null);
  const [notesOrderId, setNotesOrderId] = useState<string | null>(null);
  const [notesContent, setNotesContent] = useState<string>('');

  // Determine what the user can access based on role
  const canSeeRestaurants = user?.role === 'Resort Super Admin' ||
    user?.role === 'Partner Admin' ||
    user?.role === 'Support';
  const canSeeGroceries = user?.role === 'Resort Super Admin' ||
    user?.role === 'Partner Admin' ||
    user?.role === 'Support';

  const isSuperAdmin = user?.role === 'Resort Super Admin';
  const canManageOrders = user?.role === 'Resort Super Admin' ||
    user?.role === 'Partner Admin' ||
    user?.role === 'Support';

  // Status change dialog state
  const [statusChangeOrder, setStatusChangeOrder] = useState<DetailedOrder | null>(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Filters
  const [restaurantFilter, setRestaurantFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priceFilter, setPriceFilter] = useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = useState<string>('30');
  const [customerFilterInput, setCustomerFilterInput] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');

  // Debounce customer filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerFilter(customerFilterInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerFilterInput]);

  // Load restaurants and groceries for filter dropdown based on user role
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const promises: Promise<any>[] = [];

        if (canSeeRestaurants) {
          promises.push(listRestaurants(undefined, 1, 1000));
        } else {
          promises.push(Promise.resolve(null));
        }

        if (canSeeGroceries) {
          promises.push(listGroceries(undefined, 1, 1000));
        } else {
          promises.push(Promise.resolve(null));
        }

        const [restaurantsResult, groceriesResult] = await Promise.all(promises);

        if (restaurantsResult?.data) {
          setRestaurants(restaurantsResult.data);
        }
        if (groceriesResult?.data) {
          setGroceries(groceriesResult.data);
        }
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, [canSeeRestaurants, canSeeGroceries]);

  // Build filters object
  const buildFilters = () => {
    const filters: any = {};

    if (restaurantFilter) {
      filters.restaurant = restaurantFilter;
    }
    if (statusFilter) {
      filters.status = statusFilter;
    }
    if (priceFilter) {
      const [min, max] = priceFilter.split('-');
      if (min === '100+') {
        filters.min_price = 100;
      } else if (min && max) {
        filters.min_price = parseFloat(min);
        filters.max_price = parseFloat(max);
      }
    }
    if (dateRangeFilter && dateRangeFilter !== 'all') {
      const days = parseInt(dateRangeFilter);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      filters.start_date = startDate.toISOString().split('T')[0];
      filters.end_date = endDate.toISOString().split('T')[0];
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  // Load orders
  const loadOrders = async () => {
    setLoading(true);
    try {
      const filters = buildFilters();
      const search = customerFilter.trim() || undefined;
      const result = await listOrdersDetailed(filters, search, page, pageSize);

      if (result && result.data) {
        setOrders(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error loading orders:', error);
      setError(error.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Load stats for suspended percentage
  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const filters = buildFilters();
      const result = await getOrderStats(filters);
      if (result) {
        setStats({
          suspended_count: result.suspended_count ?? 0,
          suspended_percent: result.suspended_percent ?? 0
        });
      }
    } catch (error: any) {
      console.error('Error loading order stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [page, pageSize, restaurantFilter, statusFilter, priceFilter, dateRangeFilter, customerFilter]);

  // Handle export
  const handleExport = async () => {
    try {
      const filters = buildFilters();
      const blob = await exportOrders(filters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error exporting orders:', error);
      setError(error.message || 'Failed to export orders');
    }
  };

  // Handle confirm action (cancel or refund)
  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, orderId } = confirmAction;
    setActionLoading(orderId);
    setSuccess('');
    setError('');
    try {
      if (type === 'cancel') {
        await cancelOrder(orderId);
        setSuccess('Order cancelled');
      } else {
        await refundOrder(orderId);
        setSuccess('Refund initiated');
      }
      loadOrders();
      loadStats();
      // For refunds, poll a few times to catch the async Stripe webhook update
      if (type === 'refund') {
        setTimeout(() => { loadOrders(); }, 3000);
        setTimeout(() => { loadOrders(); }, 8000);
      }
    } catch (error: any) {
      console.error(`Error ${type === 'cancel' ? 'cancelling' : 'refunding'} order:`, error);
      setError(error.message || `Failed to ${type} order`);
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus: string, supportNote: string) => {
    if (!statusChangeOrder) return;
    setStatusChangeLoading(true);
    setSuccess('');
    setError('');
    try {
      await updateOrderStatus(statusChangeOrder.order_id, newStatus, supportNote);
      setSuccess('Order status updated');
      loadOrders();
      loadStats();
    } catch (error: any) {
      console.error('Error updating order status:', error);
      setError(error.message || 'Failed to update order status');
    } finally {
      setStatusChangeLoading(false);
      setStatusChangeOrder(null);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };

  // Toggle items expansion
  const toggleItems = (orderId: string) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  // Table columns
  const columns = [
    {
      key: 'resort_name',
      header: 'Resort',
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value || '-'}</span>
      )
    },
    {
      key: 'pos_name',
      header: 'POS',
      render: (value: string) => (
        <span className="text-gray-600">{value || '-'}</span>
      )
    },
    {
      key: 'order_id',
      header: 'Order ID',
      render: (value: string) => (
        <span className="text-gray-600">{value}</span>
      )
    },
    {
      key: 'order_date',
      header: 'Date',
      render: (value: string) => formatDate(value)
    },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (value: string, row: DetailedOrder) => (
        <div>
          <div className="font-medium text-gray-900">{value || '-'}</div>
        </div>
      )
    },
    {
      key: 'delivery_address',
      header: 'Delivery Address',
      render: (value: string) => (
        <span className="text-gray-600 max-w-xs truncate block" title={value}>{value || '-'}</span>
      )
    },
    {
      key: 'contacts',
      header: 'Contacts',
      render: (_: any, row: DetailedOrder) => (
        <div className="text-sm">
          {row.customer_phone && (
            <div className="text-gray-900">
              <a href={`tel:${row.customer_phone}`} className="hover:text-alps-primary">
                {row.customer_phone}
              </a>
            </div>
          )}
          {row.customer_email && (
            <div className="text-alps-primary">
              <a href={`mailto:${row.customer_email}`} className="hover:underline">
                {row.customer_email}
              </a>
            </div>
          )}
          {!row.customer_phone && !row.customer_email && '-'}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (value: string, row: DetailedOrder) => {
        // Show payment status instead of order status when refund is in progress or done
        const displayStatus = row.payment_status === 'Refund Pending' ? 'Refund Pending'
          : row.payment_status === 'Refunded' ? 'Refunded'
          : value;
        const statusStyles: Record<string, string> = {
          'Delivered': 'bg-green-100 text-green-800',
          'Accepted': 'bg-blue-100 text-blue-800',
          'Claimed': 'bg-blue-100 text-blue-800',
          'Ready For Delivery': 'bg-yellow-100 text-yellow-800',
          'Out For Delivery': 'bg-purple-100 text-purple-800',
          'Cancelled': 'bg-gray-100 text-gray-800',
          'Reported': 'bg-red-100 text-red-800',
          'Draft': 'bg-gray-100 text-gray-600',
          'Payment Successful': 'bg-blue-100 text-blue-800',
          'Refund Pending': 'bg-yellow-100 text-yellow-800',
          'Refunded': 'bg-green-100 text-green-800',
        };
        return (
          <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${statusStyles[displayStatus] || 'bg-gray-100 text-gray-800'}`}>
            {displayStatus}
          </span>
        );
      }
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (_: any, row: DetailedOrder) => {
        if (!row.notes) return <span className="text-gray-400">-</span>;
        return (
          <button
            onClick={() => { setNotesOrderId(row.order_id); setNotesContent(row.notes || ''); }}
            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
            title="Show Notes"
          >
            <MessageSquare className="h-3 w-3 mr-1" />
            Notes
          </button>
        );
      }
    },
    {
      key: 'items',
      header: 'Items',
      render: (items: OrderItem[], row: DetailedOrder) => {
        const isExpanded = expandedOrderId === row.order_id;
        return (
          <div>
            <button
              onClick={() => toggleItems(row.order_id)}
              className="flex items-center text-sm text-alps-primary hover:text-alps-primary-dark"
            >
              <Package className="h-4 w-4 mr-1" />
              {items?.length || 0} items
              <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </button>
            {isExpanded && items && items.length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs">
                {items.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-1 border-b border-gray-200 last:border-0">
                    <span className="text-gray-700">
                      {item.qty}x {item.item_name}
                    </span>
                    <span className="text-gray-600 ml-2">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'order_price',
      header: 'Order Price',
      render: (value: number) => (
        <span className="font-medium text-gray-900">{formatCurrency(value)}</span>
      )
    },
    ...((isSuperAdmin || canManageOrders) ? [{
      key: 'actions',
      header: 'Actions',
      render: (_: any, row: DetailedOrder) => {
        const rawStatus = row.status?.toLowerCase().replace(/ /g, '_');
        const canCancel = isSuperAdmin && (rawStatus === 'suspended' || rawStatus === 'reported' || rawStatus === 'cc_reported');
        const canRefund = isSuperAdmin && (rawStatus === 'cancelled' || rawStatus === 'cc_cancelled') && row.payment_status === 'Paid' && !!row.stripe_payment_id;
        const isLoading = actionLoading === row.order_id;

        return (
          <div className="flex gap-1">
            {canCancel && (
              <button
                onClick={() => setConfirmAction({ type: 'cancel', orderId: row.order_id })}
                disabled={isLoading}
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                title="Cancel order"
              >
                <Ban className="h-3 w-3 mr-1" />
                Cancel
              </button>
            )}
            {canRefund && (
              <button
                onClick={() => setConfirmAction({ type: 'refund', orderId: row.order_id })}
                disabled={isLoading}
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-orange-300 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                title="Refund order"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Refund
              </button>
            )}
            {canManageOrders && (
              <button
                onClick={() => setStatusChangeOrder(row)}
                disabled={isLoading}
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                title="Change status"
              >
                Change Status
              </button>
            )}
          </div>
        );
      }
    }] : [])
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Orders</h1>
          <p className="mt-1 text-sm text-gray-600">Manage and track all your orders</p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Success Alert */}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardList className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Suspended</p>
              <p className="text-2xl font-bold text-red-500">
                {statsLoading ? '-' : `${(stats?.suspended_percent ?? 0).toFixed(1)}%`}
              </p>
              <p className="text-xs text-gray-500">{stats?.suspended_count ?? 0} orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          {/* Restaurant/Grocery Filter */}
          <div className="relative">
            <select
              value={restaurantFilter}
              onChange={(e) => {
                setRestaurantFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              <option value="">
                {canSeeRestaurants && canSeeGroceries
                  ? 'All Restaurants/Groceries'
                  : canSeeGroceries
                    ? 'All Groceries'
                    : 'All Restaurants'}
              </option>
              {restaurants.map((r) => (
                <option key={r.name} value={r.name}>{r.restaurant_name}</option>
              ))}
              {groceries.length > 0 && (
                <>
                  {restaurants.length > 0 && <option disabled>---</option>}
                  {groceries.map((g) => (
                    <option key={g.name} value={g.name}>{g.grocery_name}</option>
                  ))}
                </>
              )}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Price Filter */}
          <div className="relative">
            <select
              value={priceFilter}
              onChange={(e) => {
                setPriceFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {PRICE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Date Range Filter */}
          <div className="relative">
            <select
              value={dateRangeFilter}
              onChange={(e) => {
                setDateRangeFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Customer Search Filter */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={customerFilterInput}
              onChange={(e) => setCustomerFilterInput(e.target.value)}
              placeholder="Search customer name or email..."
              className="w-full bg-white border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            />
            {customerFilterInput && (
              <button
                onClick={() => setCustomerFilterInput('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <Table
        columns={columns}
        data={orders}
        isLoading={loading}
        emptyMessage="No orders found"
        pagination={{
          page,
          page_size: pageSize,
          total
        }}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === 'cancel' ? 'Confirm Cancel' : 'Confirm Refund'}
        message={confirmAction?.type === 'cancel'
          ? 'Are you sure you want to cancel the selected order?'
          : 'Are you sure you want to refund the whole order price?'}
        confirmLabel={confirmAction?.type === 'cancel' ? 'Cancel Order' : 'Refund'}
        variant={confirmAction?.type === 'cancel' ? 'danger' : 'warning'}
        loading={!!actionLoading}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Status Change Dialog */}
      <StatusChangeDialog
        open={!!statusChangeOrder}
        orderId={statusChangeOrder?.order_id || ''}
        existingDetails={statusChangeOrder?.additional_details}
        loading={statusChangeLoading}
        onConfirm={handleStatusChange}
        onCancel={() => setStatusChangeOrder(null)}
      />

      {/* Notes Modal */}
      {notesOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setNotesOrderId(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Notes — {notesOrderId}</h3>
              <button onClick={() => setNotesOrderId(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{notesContent}</p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setNotesOrderId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
